"use client";

import Link from "next/link";
import { useEffect, useState } from "react";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [currentPath, setCurrentPath] = useState("");
  
  useEffect(() => {
    // Get current path to determine which nav item should be active
    setCurrentPath(window.location.pathname);
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header className="sticky top-0 z-50 bg-white">
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 lg:px-8">
        {/* Logo */}
        <Link href="/" className="flex items-center">
          <img 
            src="/images/logo.svg" 
            alt="E-Commerce App" 
            className="h-10 w-auto aspect-ratio-default" 
            width="120"
            height="40"
          />
        </Link>

        {/* Center Navigation */}
        <nav className="hidden items-center gap-2 md:flex">
          <Link
            href="/"
            className="flex items-center gap-2 rounded-full border border-gray-200 px-4 py-2 text-sm font-medium text-zinc-700 transition-colors hover:bg-gray-50"
          >
            {currentPath === "/" && <span className="h-2 w-2 rounded-full bg-green-500"></span>}
            Mobile App
          </Link>
          <div
            className="flex items-center gap-2 rounded-full border border-gray-200 px-4 py-2 text-sm font-medium text-zinc-400 relative opacity-70 cursor-default"
          >
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">EM BREVE</span>
            CRM
          </div>
        </nav>

        {/* Right Side */}
        <div className="hidden items-center gap-4 md:flex">
          <a
            href="#cases"
            className="text-sm font-medium text-zinc-700 transition-colors hover:text-zinc-900"
          >
            Cases
          </a>
          <a
            href="#fale-conosco"
            className="rounded-full bg-emerald-900 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-emerald-800"
          >
            Fale Conosco
          </a>
        </div>

        {/* Mobile Menu Button */}
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex h-10 w-10 items-center justify-center rounded border border-zinc-200 text-zinc-900 transition-colors hover:bg-zinc-50 md:hidden"
          aria-label="Open menu"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path d="M3 6h14" stroke="currentColor" strokeWidth="1.8" />
            <path d="M3 10h14" stroke="currentColor" strokeWidth="1.8" />
            <path d="M3 14h14" stroke="currentColor" strokeWidth="1.8" />
          </svg>
        </button>
      </div>

      {/* Mobile Menu */}
      {open && (
        <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true">
          <button
            type="button"
            className="absolute inset-0 bg-black/30"
            onClick={() => setOpen(false)}
            aria-label="Close menu"
          />
          <div className="absolute right-0 top-0 h-full w-[86%] max-w-sm bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-zinc-950">Menu</span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="inline-flex h-10 w-10 items-center justify-center rounded border border-zinc-200 text-zinc-900 hover:bg-zinc-50"
                aria-label="Close menu"
              >
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                  <path d="M4 4l10 10" stroke="currentColor" strokeWidth="1.8" />
                  <path d="M14 4 4 14" stroke="currentColor" strokeWidth="1.8" />
                </svg>
              </button>
            </div>

            <div className="mt-6 flex flex-col gap-3">
              <Link href="/" onClick={() => setOpen(false)} className="rounded px-3 py-3 text-sm font-semibold text-zinc-900 hover:bg-zinc-50 flex items-center">
                {currentPath === "/" && <span className="h-2 w-2 rounded-full bg-green-500 mr-2"></span>}
                Mobile App
              </Link>
              <div className="rounded px-3 py-3 text-sm font-semibold text-zinc-400 flex items-center relative opacity-70">
                <span className="absolute -top-2 right-2 bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">EM BREVE</span>
                CRM
              </div>
              <a href="#cases" onClick={() => setOpen(false)} className="rounded px-3 py-3 text-sm font-semibold text-zinc-900 hover:bg-zinc-50">
                Cases
              </a>
              <a href="#fale-conosco" onClick={() => setOpen(false)} className="rounded px-3 py-3 text-sm font-semibold text-zinc-900 hover:bg-zinc-50">
                Fale Conosco
              </a>
            </div>

            <a
              href="#fale-conosco"
              onClick={() => setOpen(false)}
              className="mt-7 inline-flex h-10 w-full items-center justify-center rounded-full bg-emerald-900 text-sm font-medium text-white hover:bg-emerald-800"
            >
              Fale Conosco
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
